import Axios from '@/utils/axios';

// ─── Request / Response Types ────────────────────────────────────────────────

export interface AttendanceListParams {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  search?: string;
  approvalTypes?: string; // comma-separated e.g. APPROVE,PENDING,REJECT
  providerIDs?: number[];
  pitstopIDs?: number[];
  driverTypes?: string; // comma-separated e.g. RIDER,DRIVER,WAREHOUSE
  durationTypes?: string; // comma-separated e.g. OVER_DURATION,NORMAL_DURATION
  tagIDs?: number[];
  page?: number;
  size?: number;
}

export interface AttendanceRecordAPI {
  logBookID: string;
  driverID: number;
  driverName: string;
  driverCode: string;
  driverType: string;
  driverNIK: string;
  driverStatus: string;
  driverSelfieImageUrl?: string | null; // profile photo
  clientNames: string; // comma-separated
  hubNames: string; // comma-separated
  status: string; // logbook status e.g. COMPLETED
  expressType: string;
  clockInAt: string; // ISO datetime
  clockOutAt: string | null;
  /** IANA zone of the clock-in's pitstop; null = show as WIB. */
  clockInAtPitstopTimezone?: string | null;
  /** False when the row has no untouched selfie, so its clock-in can no
   *  longer be corrected without the stamp going stale. */
  editable?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  clockOutLatitude?: number | null;
  clockOutLongitude?: number | null;
  clockOutReason: string | null;
  approvalUserID: string | null;
  approvalEmail: string | null;
  approvalFullName: string | null;
  approvalType: string; // PENDING | APPROVE | REJECT
  approvalAt: string | null;
  selfieImageUrl?: string | null; // attendance selfie
}

export interface AttendanceListResponse {
  data: AttendanceRecordAPI[];
  pagination: {
    total: string | number;
    size: number;
    page: number;
    lastPage: number;
  };
}

export interface BulkApprovalRecord {
  logBookID: string;
  approvalType: 'APPROVE' | 'REJECT';
}

export interface BulkApprovalBody {
  approvalUserID: string;
  approvalEmail: string;
  approvalFullName: string;
  records: BulkApprovalRecord[];
}

export interface BulkApprovalErrorItem {
  ID: string;
  name: string;
  message: string;
}

export interface BulkApprovalResponse {
  total: number;
  totalSuccess: number;
  totalError: number;
  error: BulkApprovalErrorItem[];
}

export interface DownloadAttendanceBody {
  approvalTypes?: string; // comma-separated e.g. APPROVE,PENDING,REJECT
  providerIDs?: number[];
  pitstopIDs?: number[];
  driverTypes?: string;
  durationTypes?: string;
  startDate?: string;
  endDate?: string;
  tagIDs?: number;
}

export interface EditAttendanceBody {
  /** ISO datetime; omit to leave the clock-in as it is. */
  clockInAt?: string;
  /** ISO datetime, or null to put the shift back to ongoing. */
  clockOutAt?: string | null;
  /** Email of the signed-in user — stored in the row's audit trail. */
  editedBy: string;
  editReason: string;
}

export interface EditAttendanceResponse {
  logBookID: string;
  clockInAt: string;
  clockOutAt: string | null;
  /** True when the selfie overlay was queued for re-stamping. */
  selfieRestamped: boolean;
}

// ─── Service ─────────────────────────────────────────────────────────────────

const AttendanceService = {
  getList: async (
    params: AttendanceListParams
  ): Promise<AttendanceListResponse> => {
    const response = await Axios.get(
      '/v1/drivers/log-book/attendance/approval',
      {
        params,

        paramsSerializer: (p) => {
          const parts: string[] = [];
          for (const [key, value] of Object.entries(p)) {
            if (value === undefined || value === null) continue;
            if (Array.isArray(value)) {
              // Send as a single comma-separated value: pitstopIDs=1,2,3
              parts.push(`${key}=${value.map(encodeURIComponent).join(',')}`);
            } else {
              parts.push(
                `${key}=${encodeURIComponent(value as string | number)}`
              );
            }
          }
          return parts.join('&');
        },
      }
    );
    // API wraps the payload as { status, message, data: { data: [], pagination: {} } }
    const payload = response.data?.data ?? response.data;
    return {
      data: payload.data ?? [],
      pagination: payload.pagination ?? {
        total: 0,
        size: 10,
        page: 1,
        lastPage: 1,
      },
    };
  },

  /**
   * Correct one record's clock-in / clock-out. Staff only, and scoped
   * server-side to the caller's own pitstops.
   */
  edit: async (
    logBookID: string,
    body: EditAttendanceBody
  ): Promise<EditAttendanceResponse> => {
    const response = await Axios.patch(
      `/v1/drivers/log-book/attendance/${logBookID}`,
      body
    );
    return response.data?.data ?? response.data;
  },

  /** Approve or reject one or more records. */
  bulkApproval: async (
    body: BulkApprovalBody
  ): Promise<BulkApprovalResponse> => {
    const response = await Axios.post(
      '/v1/drivers/log-book/record/approval/bulk',
      body
    );
    return response.data?.data ?? response.data;
  },

  /**
   * Trigger a server-side download. The API is expected to return a file blob
   * (CSV / Excel). The caller must handle the blob response.
   */
  download: async (body: DownloadAttendanceBody): Promise<Blob> => {
    const response = await Axios.post(
      '/v1/drivers/log-book/download/approval',
      body,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
};

export default AttendanceService;
